import { FileExcelOutlined, FileImageOutlined, FilePdfOutlined, FilePptOutlined, FileTextOutlined, FileWordOutlined, FileUnknownOutlined, VideoCameraOutlined } from "@ant-design/icons";

/**
 * Shared helpers for the unified attachment viewing experience.
 * Centralises file-type categorisation, PII masking detection and the
 * Office Online embed URL so every viewing mode behaves consistently.
 */

export const VIEWER_MODES = ["overlay", "modal", "drawer", "floating"];

export const VIEWER_MODE_LABELS = {
    overlay: "Overlay",
    modal: "Modal",
    drawer: "Side panel",
    floating: "Floating",
};

export const VIEWER_PREFERENCE_KEY = "pretectum.attachmentViewer.mode";

/**
 * Categorise a file purely from its MIME type / filename so all modes render
 * it the same way. Returns one of: image | video | pdf | office | text | other.
 */
export const getFileCategory = (file) => {
    const type = (file?.type || "").toLowerCase();
    const name = (file?.name || "").toLowerCase();
    const ext = name.includes(".") ? name.split(".").pop() : "";

    if (type.startsWith("image/")) return "image";
    if (type.startsWith("video/") || ["mp4", "webm", "ogg", "mov"].includes(ext)) return "video";
    if (type === "application/pdf" || ext === "pdf") return "pdf";

    const officeExts = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];
    const isOfficeType =
        type.includes("word") ||
        type.includes("officedocument") ||
        type.includes("ms-excel") ||
        type.includes("spreadsheet") ||
        type.includes("ms-powerpoint") ||
        type.includes("presentation");
    if (isOfficeType || officeExts.includes(ext)) return "office";

    if (type.startsWith("text/") || ["txt", "csv", "json", "md", "log"].includes(ext)) return "text";

    return "other";
};

export const isImageFile = (file) => getFileCategory(file) === "image";
export const isVideoFile = (file) => getFileCategory(file) === "video";
export const isPdfFile = (file) => getFileCategory(file) === "pdf";
export const isOfficeFile = (file) => getFileCategory(file) === "office";

/**
 * Detect masked PII placeholder values (e.g. "#####") so we never attempt to
 * load or render content the current user is not authorised to see.
 */
export const isMaskedValue = (value) => typeof value === "string" && /^#+$/.test(value.trim());

/**
 * Microsoft Office Online viewer can render Office documents inline as long as
 * the source is a publicly fetchable URL. The S3 pre-signed URLs satisfy this.
 */
export const getOfficeViewerUrl = (url) => `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

export const getFileTypeIcon = (fileType = "", className = "") => {
    const type = fileType.toLowerCase();
    if (type.startsWith("image/")) return <FileImageOutlined className={`text-blue-500 ${className}`} />;
    if (type.startsWith("video/")) return <VideoCameraOutlined className={`text-purple-500 ${className}`} />;
    if (type === "application/pdf") return <FilePdfOutlined className={`text-red-500 ${className}`} />;
    if (type.includes("word") || type.includes("document")) return <FileWordOutlined className={`text-blue-600 ${className}`} />;
    if (type.includes("sheet") || type.includes("excel")) return <FileExcelOutlined className={`text-green-600 ${className}`} />;
    if (type.includes("presentation") || type.includes("powerpoint")) return <FilePptOutlined className={`text-orange-500 ${className}`} />;
    if (type.startsWith("text/")) return <FileTextOutlined className={`text-gray-500 ${className}`} />;
    return <FileUnknownOutlined className={`text-gray-400 ${className}`} />;
};
